import { CustomError, ErrorCode } from 'src/error';

export type UserFacingErrorKind =
  | 'network'
  | 'server'
  | 'not-found'
  | 'unauthorized'
  | 'permission'
  | 'validation'
  | 'rate-limit'
  | 'aborted'
  | 'unknown';

export interface UserFacingError {
  kind: UserFacingErrorKind;
  title: string;
  message: string;
  canRetry: boolean;
  safeDetail?: string;
}

const FALLBACK_MESSAGES: Record<
  UserFacingErrorKind,
  {
    title: string;
    message: string;
    canRetry: boolean;
  }
> = {
  network: {
    title: 'Verbindingsprobleem',
    message: 'We kunnen de server niet bereiken. Controleer je internetverbinding en probeer opnieuw.',
    canRetry: true,
  },
  server: {
    title: 'Serverfout',
    message: 'De server kon je verzoek niet verwerken. Probeer het later opnieuw.',
    canRetry: true,
  },
  'not-found': {
    title: 'Niet gevonden',
    message: 'De gevraagde gegevens bestaan niet meer of zijn niet beschikbaar.',
    canRetry: false,
  },
  unauthorized: {
    title: 'Sessie verlopen',
    message: 'Je sessie is verlopen of je bent niet ingelogd. Log opnieuw in om verder te gaan.',
    canRetry: false,
  },
  permission: {
    title: 'Geen toegang',
    message: 'Je hebt geen toegang tot deze gegevens.',
    canRetry: false,
  },
  validation: {
    title: 'Ongeldige gegevens',
    message: 'Controleer de ingevulde gegevens en probeer opnieuw.',
    canRetry: false,
  },
  'rate-limit': {
    title: 'Te veel verzoeken',
    message: 'Er zijn te veel verzoeken verstuurd. Wacht even en probeer opnieuw.',
    canRetry: true,
  },
  aborted: {
    title: 'Verzoek geannuleerd',
    message: 'Het verzoek werd geannuleerd. Probeer opnieuw indien nodig.',
    canRetry: true,
  },
  unknown: {
    title: 'Onbekende fout',
    message: 'Er is een onverwachte fout opgetreden. Probeer opnieuw of contacteer support als dit blijft gebeuren.',
    canRetry: true,
  },
};

const TECHNICAL_MESSAGE_PATTERNS = [
  /\bhttps?:\/\//i,
  /(^|\s)\/api\/v\d+\//i,
  /\b[a-z]+:\/\/\S+/i,
  /\bstack\b/i,
  /\bprisma\b/i,
  /\bsyntaxerror\b/i,
  /\btypeerror\b/i,
  /\bnullthrows\b/i,
  /\binvariant\b/i,
  /\bundefined\b/i,
  /\bobject object\b/i,
];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function getStringProperty(value: unknown, property: string): string | undefined {
  if (!isRecord(value)) {
    return undefined;
  }

  const propertyValue = value[property];
  return typeof propertyValue === 'string' ? propertyValue : undefined;
}

function isOffline(): boolean {
  return typeof globalThis.navigator !== 'undefined' && globalThis.navigator.onLine === false;
}

function getErrorCode(err: unknown): string | undefined {
  if (err instanceof CustomError) {
    return err.code;
  }

  return getStringProperty(err, 'code');
}

function getErrorName(err: unknown): string | undefined {
  return getStringProperty(err, 'name');
}

function getRawMessage(err: unknown): string | undefined {
  if (typeof err === 'string') {
    return err;
  }

  if (typeof err === 'number' || typeof err === 'boolean') {
    return `${err}`;
  }

  if (err instanceof Error) {
    return err.message;
  }

  const directMessage = getStringProperty(err, 'message');
  if (directMessage) {
    return directMessage;
  }

  if (isRecord(err)) {
    const errors = err.errors;
    if (Array.isArray(errors) && errors.every((error) => typeof error === 'string')) {
      return errors.join(', ');
    }

    const graphQLErrors = err.graphQLErrors;
    const firstGraphQLError = Array.isArray(graphQLErrors) ? graphQLErrors[0] : undefined;
    const graphQLErrorMessage = getStringProperty(firstGraphQLError, 'message');
    if (graphQLErrorMessage) {
      return graphQLErrorMessage;
    }
  }

  return undefined;
}

function getKindFromCode(code: string | undefined): UserFacingErrorKind | undefined {
  switch (code) {
    case ErrorCode.NOT_FOUND:
    case 'not-found':
    case 'NotFound':
      return 'not-found';
    case ErrorCode.UNAUTHORIZED:
    case 'unauthorized':
    case 'invalid-token':
      return 'unauthorized';
    case ErrorCode.PERMISSION_DENIED:
    case 'permission-denied':
      return 'permission';
    case ErrorCode.VALIDATION_ERROR:
    case ErrorCode.UNEXPECTED_METHOD:
    case 'validation-error':
      return 'validation';
    case ErrorCode.RATE_LIMIT_EXCEEDED:
      return 'rate-limit';
    case ErrorCode.ABORTED:
      return 'aborted';
    case ErrorCode.UNEXPECTED_ERROR:
    case 'unknown':
      return 'server';
    default:
      return undefined;
  }
}

function getKindFromMessage(message: string | undefined, name: string | undefined): UserFacingErrorKind | undefined {
  if (name === 'AbortError') {
    return 'aborted';
  }

  if (isOffline()) {
    return 'network';
  }

  if (!message) {
    return undefined;
  }

  const normalized = message.toLowerCase();
  if (
    normalized.includes('failed to fetch') ||
    normalized.includes('networkerror') ||
    normalized.includes('load failed') ||
    normalized.includes('network request failed') ||
    normalized.includes('no response received')
  ) {
    return 'network';
  }

  if (
    normalized.includes('request failed: internal server error') ||
    normalized.includes('request failed: 500') ||
    normalized.includes('server error')
  ) {
    return 'server';
  }

  if (normalized.includes('not found') || normalized.includes('niet gevonden')) {
    return 'not-found';
  }

  if (normalized.includes('unauthorized') || normalized.includes('not logged in')) {
    return 'unauthorized';
  }

  if (normalized.includes('forbidden') || normalized.includes('permission denied')) {
    return 'permission';
  }

  return undefined;
}

function sanitizeDetail(message: string | undefined): string | undefined {
  const trimmed = message?.replace(/\s+/g, ' ').trim();
  if (!trimmed) {
    return undefined;
  }

  if (trimmed.length > 240) {
    return undefined;
  }

  if (TECHNICAL_MESSAGE_PATTERNS.some((pattern) => pattern.test(trimmed))) {
    return undefined;
  }

  return trimmed;
}

export function getUserFacingError(err: unknown): UserFacingError {
  const code = getErrorCode(err);
  const rawMessage = getRawMessage(err);
  const kind = getKindFromCode(code) ?? getKindFromMessage(rawMessage, getErrorName(err)) ?? 'unknown';
  const fallback = FALLBACK_MESSAGES[kind];
  const safeDetail = kind === 'validation' ? sanitizeDetail(rawMessage) : undefined;

  return {
    kind,
    title: fallback.title,
    message: safeDetail ?? fallback.message,
    canRetry: fallback.canRetry,
    safeDetail,
  };
}

export function getDisplayError(err: unknown): string {
  return getUserFacingError(err).message;
}
